import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/hooks/useAuthContext';
import { env } from '@/lib/env';
import type { UserRole } from '@/types';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
}

export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuthContext();
  const location = useLocation();

  // Debug logging (dev only)
  if (env.isDev) {
    console.log('🛡️ ProtectedRoute check:', {
      path: location.pathname,
      isAuthenticated,
      isLoading,
      userEmail: user?.email,
      userRole: user?.role,
      allowedRoles,
      isAllowed: allowedRoles ? allowedRoles.includes(user?.role as UserRole) : true,
      willRedirect: !isAuthenticated && !isLoading
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (env.isDev) console.log('❌ Not authenticated, redirecting to login');
    // Redirect to login, but save the location they were trying to access
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Check role-based access
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Access Denied</h2>
          <p className="text-neutral-600 mb-4">
            You don't have permission to access this page. Your role ({user.role}) is not authorized.
          </p>
          <a
            href="/"
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
